/**
 * Identity Match — master-only panel. Configures the identity-resolution
 * pixel for this dealer. Dealer never sees this tab or any indication a
 * cap exists — this entire panel lives inside the master-only dealer
 * detail page.
 *
 * NOTE: no vendor name appears anywhere in this UI by design — only
 * generic "Identity Match" labeling.
 */
import { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Loader2, Save, ScanSearch } from "lucide-react";

export default function IdentityMatchAdminPanel({ dealer, onRefresh }) {
  const { apiFetch } = useAuth();
  const { success: toastOk, error: toastErr } = useToast();
  const [subclientId, setSubclientId] = useState(dealer?.sera_subclient_id || "");
  const [pixelScope, setPixelScope] = useState(dealer?.sera_pixel_scope || "vdp_only");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSubclientId(dealer?.sera_subclient_id || "");
    setPixelScope(dealer?.sera_pixel_scope || "vdp_only");
  }, [dealer?.sera_subclient_id, dealer?.sera_pixel_scope]);

  const usage = dealer?.sera_usage;
  const pctUsed = usage?.pct_used ?? 0;
  const nearCap = pctUsed >= 90;

  const save = async () => {
    setSaving(true);
    try {
      const res = await apiFetch(`/admin/dealers/${dealer.id}/info`, {
        method: "PATCH",
        data: { sera_subclient_id: subclientId.trim() || null, sera_pixel_scope: pixelScope },
      });
      const vv = res.data?.vendor_validation;
      if (vv?.skipped) {
        toastOk("Saved — vendor validation skipped (API key not configured yet).");
      } else {
        toastOk("Identity Match settings saved.");
      }
      onRefresh?.();
    } catch (e) {
      toastErr(e?.response?.data?.detail || "Could not save Identity Match settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="identity-match-panel">
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-[13px] text-slate-700">
        <div className="font-semibold text-slate-900 mb-1">
          Identity Match · Master-only · Dealer sees none of this
        </div>
        <div className="leading-relaxed">
          Configures a passive identity-resolution pixel for this dealer. Matched
          contacts land in the dealer&rsquo;s &ldquo;Identified Visitors&rdquo; section &mdash; never in
          Customer Leads &mdash; until they reply.
        </div>
      </div>

      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-[15px] font-semibold flex items-center gap-2">
            <ScanSearch size={16} /> Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="identity-match-subclient-id" className="text-[12px] font-medium text-slate-700">
              Identity Match Subclient ID
            </Label>
            <Input
              id="identity-match-subclient-id"
              value={subclientId}
              onChange={(e) => setSubclientId(e.target.value)}
              placeholder="e.g. tampaford"
              className="mt-1 font-mono text-[13px]"
              data-testid="identity-match-subclient-input"
            />
            <p className="text-[11.5px] text-slate-400 mt-1">
              Leave blank to keep the pixel off for this dealer.
            </p>
          </div>

          <div>
            <Label className="text-[12px] font-medium text-slate-700">Pixel Scope</Label>
            <Select value={pixelScope} onValueChange={setPixelScope}>
              <SelectTrigger className="mt-1" data-testid="identity-match-scope-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="vdp_only">VDP Only</SelectItem>
                <SelectItem value="all_pages">All Pages</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {subclientId && (
            <div className="pt-2 border-t border-slate-100">
              <div className="flex items-center justify-between">
                <span className="text-[12px] font-medium text-slate-700">Usage this cycle</span>
                {nearCap && (
                  <span className="text-[11px] font-semibold text-red-600" data-testid="identity-match-cap-warning">
                    ⚠ Near cap
                  </span>
                )}
              </div>
              <p className={`text-[13px] font-mono mt-1 ${nearCap ? "text-red-600" : "text-slate-600"}`}
                 data-testid="identity-match-usage-count">
                {usage ? `${usage.running_count} / ${usage.monthly_cap}` : "0 / 2000"}
              </p>
            </div>
          )}

          <Button onClick={save} disabled={saving} data-testid="identity-match-save-btn">
            {saving ? <Loader2 className="animate-spin mr-1.5" size={14} /> : <Save size={14} className="mr-1.5" />}
            Save changes
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
